import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import {
  Server,
  Database,
  HardDrive,
  Activity,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
} from "lucide-react";
import axios from "axios";
import logger from "../../utils/logger";

const SystemHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const fetchSystemHealth = async () => {
    try {
      const token = localStorage.getItem("superAdminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/system/health`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setHealthData(response.data.data);
    } catch (error) {
      logger.error("Error fetching system health:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSystemHealth();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-slate-600">جاري تحميل حالة النظام...</p>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "unhealthy":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "bg-green-100 text-green-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "unhealthy":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">حالة النظام</h1>
          <p className="text-slate-600 mt-1">
            مراقبة صحة النظام والأداء
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          {refreshing ? "جاري التحديث..." : "تحديث"}
        </Button>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">قاعدة البيانات</CardTitle>
            <Database className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getStatusIcon(healthData?.database)}
              <span className="text-2xl font-bold">
                {healthData?.database === "healthy" ? "صحية" : "غير صحية"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">مساحة التخزين</CardTitle>
            <HardDrive className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getStatusIcon(healthData?.disk_space)}
              <span className="text-2xl font-bold">
                {healthData?.disk_space === "healthy" ? "صحية" : "غير صحية"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الذاكرة</CardTitle>
            <Server className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getStatusIcon(healthData?.memory)}
              <span className="text-2xl font-bold">
                {healthData?.memory === "healthy" ? "صحية" : "غير صحية"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ال_warnings</CardTitle>
            <AlertTriangle className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {healthData?.warnings?.length || 0}
            </div>
            <p className="text-xs text-slate-600">تنبيهات نشطة</p>
          </CardContent>
        </Card>
      </div>

      {/* Warnings Section */}
      {healthData?.warnings?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              تنبيهات النظام
            </CardTitle>
            <CardDescription>
              مشاكل تتطلب انتباهك الفوري
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {healthData.warnings.map((warning, index) => (
                <Alert key={index} variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>تنبيه</AlertTitle>
                  <AlertDescription>{warning}</AlertDescription>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed System Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              معلومات النظام
            </CardTitle>
            <CardDescription>
              تفاصيل تقنية حول النظام
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-slate-600">حالة الخادم</span>
                <Badge className={getStatusColor(healthData?.database)}>
                  {healthData?.database === "healthy" ? "يعمل" : "مشكلة"}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-slate-600">مساحة التخزين</span>
                <Badge className={getStatusColor(healthData?.disk_space)}>
                  {healthData?.disk_space === "healthy" ? "طبيعية" : "محدودة"}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-slate-600">الذاكرة</span>
                <Badge className={getStatusColor(healthData?.memory)}>
                  {healthData?.memory === "healthy" ? "طبيعية" : "منخفضة"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              إحصائيات النظام
            </CardTitle>
            <CardDescription>
              معلومات إضافية عن أداء النظام
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-slate-600">عدد التنبيهات</span>
                <span className="font-medium">{healthData?.warnings?.length || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-slate-600">آخر تحديث</span>
                <span className="font-medium">
                  {new Date().toLocaleTimeString("ar-SA")}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SystemHealth;